import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import { type DiagnosedIcd, type DiagnosisSelectionChoice, type ResultData } from 'types/result';
import type { IcdStatus } from 'hooks/useRecordingWebSocket';
import { useDebouncedFieldAutosave } from 'hooks/useDebouncedFieldAutosave';
import type { EditorAutosaveDeps } from 'hooks/useAutosaveStatus';
import {
  buildDiagnosedIcdSelectionKey,
  normalizeDiagnosedIcdEntry,
  sanitizeDiagnosedIcdsForApi,
} from 'widgets/results/diagnosis.utils';

const DIAGNOSED_ICD_SECTION_KEY = 'diagnosedIcdsSection';

export interface UseDiagnosedIcdsEditorProps extends EditorAutosaveDeps {
  result: ResultData | undefined;
  resultId: string | number | undefined;
  fromRecording: boolean;
  icdCodes: DiagnosedIcd[] | null;
  icdStatus: IcdStatus;
  t: TFunction;
}

export interface UseDiagnosedIcdsEditorReturn {
  editableDiagnosedIcds: DiagnosedIcd[];
  hasDiagnosedIcds: boolean;
  icdPending: boolean;
  diagnosedIcdsTitle: string;
  selectionStates: boolean[];
  onToggleSelection: (index: number, checked: boolean) => void;
  handleDiagnosedIcdChange: (
    index: number,
    field: 'category' | 'sicherheit' | 'seite',
    value: string
  ) => void;
  pendingDiagnosisSelection: () => Record<string, DiagnosisSelectionChoice> | null;
  icdCompleteBannerVisible: boolean;
  dismissIcdCompleteBanner: () => void;
}

export function useDiagnosedIcdsEditor({
  result,
  resultId,
  fromRecording,
  icdCodes,
  icdStatus,
  autosaveEditAsync,
  autosaveTimeouts,
  setAutoStatus,
  setHasUserEdits,
  t,
}: UseDiagnosedIcdsEditorProps): UseDiagnosedIcdsEditorReturn {
  const [editableDiagnosedIcds, setEditableDiagnosedIcds] = useState<DiagnosedIcd[]>([]);
  const [diagnosedIcdSelectionMap, setDiagnosedIcdSelectionMap] = useState<Record<string, boolean>>(
    {}
  );
  const hasShownIcdCompleteBannerRef = useRef(false);
  const [icdCompleteBannerVisible, setIcdCompleteBannerVisible] = useState(false);

  const resultSourcedDiagnosedIcds = useMemo(() => {
    if (!result || !Array.isArray(result.diagnosedIcds)) return [];
    return result.diagnosedIcds.map((entry) => normalizeDiagnosedIcdEntry(entry));
  }, [result]);

  const sourceDiagnosedIcds = useMemo(() => {
    if (icdCodes === null) {
      return resultSourcedDiagnosedIcds;
    }
    const icdCodeKeys = new Set(icdCodes.map(buildDiagnosedIcdSelectionKey));
    const missingFromIcdCodes = resultSourcedDiagnosedIcds.filter(
      (entry) => !icdCodeKeys.has(buildDiagnosedIcdSelectionKey(entry))
    );
    return [...icdCodes, ...missingFromIcdCodes];
  }, [resultSourcedDiagnosedIcds, icdCodes]);

  useEffect(() => {
    if (!fromRecording || icdCodes === null || hasShownIcdCompleteBannerRef.current) return;
    hasShownIcdCompleteBannerRef.current = true;
    setIcdCompleteBannerVisible(true);
  }, [fromRecording, icdCodes]);

  useEffect(() => {
    if (!icdCompleteBannerVisible) return;
    const timeout = setTimeout(() => setIcdCompleteBannerVisible(false), 3000);
    return () => clearTimeout(timeout);
  }, [icdCompleteBannerVisible]);

  const interactedDiagnosisIdsRef = useRef<Set<number>>(new Set());
  const surfacedIcdKeysRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    interactedDiagnosisIdsRef.current = new Set();
    surfacedIcdKeysRef.current = new Set();
    setEditableDiagnosedIcds([]);
    setDiagnosedIcdSelectionMap({});
  }, [resultId]);

  const editableDiagnosedIcdsRef = useRef(editableDiagnosedIcds);
  useEffect(() => {
    editableDiagnosedIcdsRef.current = editableDiagnosedIcds;
  }, [editableDiagnosedIcds]);

  const buildDiagnosisSelectionMap = useCallback((): Record<string, DiagnosisSelectionChoice> => {
    const map: Record<string, DiagnosisSelectionChoice> = {};
    editableDiagnosedIcdsRef.current.forEach((entry) => {
      if (!entry.id || !interactedDiagnosisIdsRef.current.has(entry.id)) return;
      const key = buildDiagnosedIcdSelectionKey(entry);
      const checked = diagnosedIcdSelectionMap[key] ?? true;
      map[entry.id] = checked ? 'selected' : 'deselected';
    });
    return map;
  }, [diagnosedIcdSelectionMap]);

  const persistDiagnosisSelection = useCallback(
    (selection: Record<string, DiagnosisSelectionChoice>) =>
      autosaveEditAsync({ diagnosisSelection: selection }),
    [autosaveEditAsync]
  );
  const diagnosisSelectionAutosave = useDebouncedFieldAutosave(
    'diagnosisSelection',
    300,
    autosaveTimeouts,
    setAutoStatus,
    persistDiagnosisSelection
  );

  useEffect(() => {
    if (interactedDiagnosisIdsRef.current.size === 0) return;
    diagnosisSelectionAutosave.schedule(buildDiagnosisSelectionMap);
  }, [diagnosedIcdSelectionMap, buildDiagnosisSelectionMap, diagnosisSelectionAutosave]);

  const pendingDiagnosisSelection = useCallback((): Record<
    string,
    DiagnosisSelectionChoice
  > | null => {
    clearTimeout(autosaveTimeouts.current['diagnosisSelection']);
    const selection = buildDiagnosisSelectionMap();
    return Object.keys(selection).length > 0 ? selection : null;
  }, [autosaveTimeouts, buildDiagnosisSelectionMap]);

  useEffect(() => {
    setDiagnosedIcdSelectionMap((prev) => {
      const next: Record<string, boolean> = {};
      sourceDiagnosedIcds.forEach((entry) => {
        const key = buildDiagnosedIcdSelectionKey(entry);
        next[key] = Object.prototype.hasOwnProperty.call(prev, key)
          ? prev[key]
          : (entry.selected ?? true);
      });
      return next;
    });
  }, [sourceDiagnosedIcds]);

  // Merges only newly-surfaced ICDs into `editableDiagnosedIcds`, tracked via `surfacedIcdKeysRef`
  // so a locally-removed entry doesn't resurface on the next update.
  useEffect(() => {
    const newlySurfaced = sourceDiagnosedIcds.filter(
      (entry) => !surfacedIcdKeysRef.current.has(buildDiagnosedIcdSelectionKey(entry))
    );
    if (newlySurfaced.length === 0) return;
    newlySurfaced.forEach((entry) => {
      surfacedIcdKeysRef.current.add(buildDiagnosedIcdSelectionKey(entry));
    });
    setEditableDiagnosedIcds((prev) => [...prev, ...newlySurfaced.map((entry) => ({ ...entry }))]);
  }, [sourceDiagnosedIcds]);

  const hasDiagnosedIcds = sourceDiagnosedIcds.length > 0;
  const icdPending = icdStatus === 'pending';
  const diagnosedIcdsTitle = t('result.section.diagnosis');

  const getSelectionStatesForList = useCallback(
    (list: DiagnosedIcd[]) =>
      list.map((entry) => {
        const key = buildDiagnosedIcdSelectionKey(entry);
        return diagnosedIcdSelectionMap[key] ?? entry.selected ?? true;
      }),
    [diagnosedIcdSelectionMap]
  );

  const handleDiagnosedIcdSelectionToggle = useCallback(
    (list: DiagnosedIcd[], index: number, checked: boolean) => {
      const entry = list[index];
      if (!entry) {
        return;
      }
      const key = buildDiagnosedIcdSelectionKey(entry);
      if (entry.id) {
        interactedDiagnosisIdsRef.current.add(entry.id);
      }
      setDiagnosedIcdSelectionMap((prev) =>
        prev[key] === checked ? prev : { ...prev, [key]: checked }
      );
    },
    []
  );

  const scheduleDiagnosedIcdsAutosave = useCallback(() => {
    clearTimeout(autosaveTimeouts.current[DIAGNOSED_ICD_SECTION_KEY]);
    autosaveTimeouts.current[DIAGNOSED_ICD_SECTION_KEY] = setTimeout(() => {
      setAutoStatus((prev) => ({ ...prev, [DIAGNOSED_ICD_SECTION_KEY]: 'saving' }));
      void autosaveEditAsync({
        diagnosedIcds: sanitizeDiagnosedIcdsForApi(editableDiagnosedIcdsRef.current),
      })
        .then(() => setAutoStatus((prev) => ({ ...prev, [DIAGNOSED_ICD_SECTION_KEY]: 'saved' })))
        .catch(() => setAutoStatus((prev) => ({ ...prev, [DIAGNOSED_ICD_SECTION_KEY]: 'error' })));
    }, 1000);
  }, [autosaveEditAsync, autosaveTimeouts, setAutoStatus]);

  const handleDiagnosedIcdChange = useCallback(
    (index: number, field: 'category' | 'sicherheit' | 'seite', value: string) => {
      setEditableDiagnosedIcds((prev) => {
        if (index < 0 || index >= prev.length) {
          return prev;
        }
        const next = [...prev];
        next[index] = normalizeDiagnosedIcdEntry({
          ...prev[index],
          [field]: value,
        });
        return next;
      });
      scheduleDiagnosedIcdsAutosave();
      handleDiagnosedIcdSelectionToggle(editableDiagnosedIcdsRef.current, index, true);
      setHasUserEdits(true);
      setAutoStatus((prev) => ({ ...prev, [DIAGNOSED_ICD_SECTION_KEY]: 'typing' }));
    },
    [
      scheduleDiagnosedIcdsAutosave,
      handleDiagnosedIcdSelectionToggle,
      setHasUserEdits,
      setAutoStatus,
    ]
  );

  const selectionStates = useMemo(
    () => getSelectionStatesForList(editableDiagnosedIcds),
    [getSelectionStatesForList, editableDiagnosedIcds]
  );
  const onToggleSelection = useCallback(
    (index: number, checked: boolean) => {
      handleDiagnosedIcdSelectionToggle(editableDiagnosedIcds, index, checked);
    },
    [handleDiagnosedIcdSelectionToggle, editableDiagnosedIcds]
  );

  const dismissIcdCompleteBanner = useCallback(() => setIcdCompleteBannerVisible(false), []);

  return useMemo(
    () => ({
      editableDiagnosedIcds,
      hasDiagnosedIcds,
      icdPending,
      diagnosedIcdsTitle,
      selectionStates,
      onToggleSelection,
      handleDiagnosedIcdChange,
      pendingDiagnosisSelection,
      icdCompleteBannerVisible,
      dismissIcdCompleteBanner,
    }),
    [
      editableDiagnosedIcds,
      hasDiagnosedIcds,
      icdPending,
      diagnosedIcdsTitle,
      selectionStates,
      onToggleSelection,
      handleDiagnosedIcdChange,
      pendingDiagnosisSelection,
      icdCompleteBannerVisible,
      dismissIcdCompleteBanner,
    ]
  );
}
